/**
 * Client-side stereo transfer: when a detection is annotated on one camera,
 * warp it onto the other camera using the selected {@link StereoMatcher} — the
 * VIAME "match" ONNX model or the Fast-FoundationStereo export — with no
 * backend, so it works in both the web and desktop DIVE builds.
 *
 * This mirrors the desktop backend stereo handler (ViewerLoader's
 * `handleStereoAnnotationComplete`) but runs the correspondence search and the
 * triangulation in the browser. Boxes, head/tail lines and polygons transfer;
 * segmentation seeds need SAM and so remain a backend-only feature.
 *
 * Pixel access and calibration/model loading are injected so this stays
 * platform-agnostic and unit-testable; the web ViewerLoader supplies the
 * concrete providers.
 */

import CameraStore from 'vue-media-annotator/CameraStore';
import Track from 'vue-media-annotator/track';
import {
  headTailFeatures, sampleHeadTail, distanceToHeadTail, isHeadTailPoint,
} from 'vue-media-annotator/headTail';
import { RectBounds } from 'vue-media-annotator/utils';
import { HeadTailLineKey } from 'dive-common/recipes/headtail';
import type { StereoAnnotationCompleteParams } from '../useModeManager';
import {
  canMapPoint, pointUnchanged, applyMappedPoint, pointTargetState, detectionTransferJob, unmappedPoints,
} from './keypointTransfer';
import type { SearchRange } from './StereoOnnxMatcher';
import type { StereoMatcher } from './stereoMatcher';
import { StereoRig, invertRig } from './calibration';
import { RgbaImage } from './image';
import { measureLine, aggregateLengths, StereoMeasurement } from './triangulate';

export interface StereoOnnxTransferConfig {
  cameraStore: CameraStore;
  /** Names of all cameras (transfer only runs when there are at least two). */
  getMultiCamList: () => string[];
  /** Which DIVE camera corresponds to the rig's left (calibration) camera. */
  getLeftCameraName: () => string;
  /** Stereo calibration, or null if unavailable (transfer is then skipped). */
  getRig: () => Promise<StereoRig | null>;
  /**
   * The (lazily created / cached) matcher for the selected method, or null if
   * unavailable. Either correspondence method satisfies {@link StereoMatcher},
   * so nothing downstream branches on which one is in use.
   */
  /** `imagery` is the frame size, so a size-dependent model can be chosen. */
  getMatcher: (imagery?: { width: number; height: number }) => Promise<StereoMatcher | null>;
  /** Full-resolution RGBA pixels for a camera at a frame, or null. */
  getFrame: (cameraName: string, frameNum: number) => Promise<RgbaImage | null>;
  /** Disparity- or depth-based search range for the correspondence search. */
  getRange: () => SearchRange;
  threshold?: number;
  uniquenessRatio?: number;
  /** Warp an annotation onto the camera that has no detection for it yet. */
  autoCompute?: () => boolean;
  /** Triangulate and store length attributes once both cameras have a line. */
  measureLengths?: () => boolean;
  /** Called after a feature is written so the host can persist the change. */
  onChange?: (cameraName: string, track: Track) => void;
  /** Progress message while a transfer runs; null clears it. */
  onStatus?: (message: string | null) => void;
  /** Surfaced failure, mirroring the desktop stereo error dialog. */
  onError?: (message: string) => void;
  /** Reports the latest computed measurement for a transient notification. */
  onMeasurement?: (measurement: StereoMeasurement) => void;
  /** Register the measurement attribute definitions before the first write. */
  ensureMeasurementAttributes?: () => void;
}

const BOX_PAD = 0.10;
const MAX_BOX_ASPECT_RATIO = 6;

/**
 * Per-feature marker: a human (not the stereo warp) authored this camera's line
 * at this frame. Once set, the warp never overwrites that side's geometry.
 */
export const STEREO_USER_LINE_ATTR = 'stereo_user_line';
/** 'stereo' = auto-computed from the warped lines, 'user_set' = locked by the user. */
export const STEREO_LENGTH_METHOD_ATTR = 'length_method';
export const STEREO_MEASUREMENT_ATTRS = [
  'length', 'curved_length', 'straight_length', 'curvature_ratio', 'midpoint_x', 'midpoint_y', 'midpoint_z', 'midpoint_range', 'stereo_rms',
] as const;

type Point = [number, number];
type Line = Point[];

const round2 = (v: number) => Math.round(v * 100) / 100;

export default function useStereoOnnxTransfer(config: StereoOnnxTransferConfig) {
  const {
    cameraStore, getMultiCamList, getLeftCameraName,
    getRig, getMatcher, getFrame, getRange,
  } = config;

  const autoCompute = () => config.autoCompute?.() ?? true;
  const measureLengths = () => config.measureLengths?.() ?? false;

  function getOrCreateTrack(trackId: number, sourceCamera: string, targetCamera: string, frameNum: number): Track | undefined {
    return cameraStore.getPossibleTrack(trackId, targetCamera)
      ?? cameraStore.addLinkedTrack(trackId, targetCamera, frameNum, sourceCamera);
  }

  /**
   * Bounds around a point set. `pad` expands by a fraction of the extent and
   * `capAspect` grows the short side so a near-axis-aligned line does not become
   * a razor-thin box (matching headtail.ts).
   */
  function boundsFromPoints(pts: Point[], pad = 0, capAspect = false): RectBounds {
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    const padX = (maxX - minX) * pad || (maxY - minY) * pad;
    const padY = (maxY - minY) * pad || (maxX - minX) * pad;
    let [x0, y0, x1, y1] = [minX - padX, minY - padY, maxX + padX, maxY + padY];
    if (capAspect) {
      const w = x1 - x0;
      const h = y1 - y0;
      if (w > 0 && h > 0) {
        if (w / h > MAX_BOX_ASPECT_RATIO) {
          const grow = (w / MAX_BOX_ASPECT_RATIO - h) / 2;
          y0 -= grow;
          y1 += grow;
        } else if (h / w > MAX_BOX_ASPECT_RATIO) {
          const grow = (h / MAX_BOX_ASPECT_RATIO - w) / 2;
          x0 -= grow;
          x1 += grow;
        }
      }
    }
    return [x0, y0, x1, y1];
  }

  function lineEndpoints(track: Track | undefined, frameNum: number): Line | null {
    if (!track) return null;
    const [feature] = track.getFeature(frameNum);
    const features = feature?.geometry?.features;
    if (!features) return null;
    const lineFeat = features.find(
      (f) => f.geometry.type === 'LineString'
        && (f.geometry.coordinates as Point[]).length >= 2,
    );
    if (!lineFeat) return null;
    const c = lineFeat.geometry.coordinates as unknown as Point[];
    return c;
  }

  /** Replace a track feature's head/tail line, preserving any other geometry. */
  function applyLine(track: Track | undefined, frameNum: number, line: Line, sourceCamera: string) {
    if (!track) return;
    const geometry = headTailFeatures(line).map((g) => ({
      ...g,
      properties: g.geometry.type === 'Point'
        ? { ...g.properties, stereoSource: sourceCamera, stereoKey: g.properties?.key } : g.properties,
    }));
    track.setFeature({
      frame: frameNum,
      flick: 0,
      keyframe: true,
      interpolate: false,
      bounds: boundsFromPoints(line, BOX_PAD, true),
    }, geometry as GeoJSON.Feature<GeoJSON.Geometry>[] as never);
  }

  /**
   * Store a measurement on a feature: length goes to the canonical fishLength
   * (the VIAME CSV length column) plus a 'length' attribute; the rest become
   * detection attributes. A length the user locked is never overwritten.
   */
  function applyMeasurement(track: Track | undefined, frameNum: number, m: StereoMeasurement) {
    if (!track) return;
    const [feature] = track.getFeature(frameNum);
    track.setFeatureAttribute(frameNum, 'measurement_stale', false);
    const lengthLocked = feature?.attributes?.[STEREO_LENGTH_METHOD_ATTR] === 'user_set';
    if (!lengthLocked && Number.isFinite(m.length) && feature?.keyframe) {
      track.setFeature({ frame: frameNum, fishLength: round2(m.length) });
      track.setFeatureAttribute(frameNum, STEREO_LENGTH_METHOD_ATTR, 'stereo');
    }
    STEREO_MEASUREMENT_ATTRS.forEach((name) => {
      if (name === 'length' && lengthLocked) return;
      const v = m[name as keyof StereoMeasurement];
      if (typeof v === 'number' && Number.isFinite(v)) track.setFeatureAttribute(frameNum, name, round2(v));
    });
  }

  function updateTrackAverage(track: Track | undefined) {
    if (!track) return;
    const lengths: number[] = [];
    track.featureIndex.forEach((frame) => {
      const feature = track.features[frame];
      const attr = feature?.attributes?.length;
      const fromAttr = attr !== undefined && attr !== null ? Number(attr) : NaN;
      const length = Number.isFinite(fromAttr) ? fromAttr : feature?.fishLength;
      if (Number.isFinite(length)) lengths.push(length as number);
    });
    const avg = aggregateLengths(lengths);
    if (avg !== null) track.setAttribute('avg_length', round2(avg));
  }

  function updateTrackAverages(trackId: number) {
    getMultiCamList().forEach((camera) => {
      updateTrackAverage(cameraStore.getPossibleTrack(trackId, camera));
    });
  }

  /**
   * Triangulate the head/tail lines both cameras hold at this frame and write
   * the measurement to each. The rig is always oriented with the calibration's
   * left camera first, independent of which camera the user drew on.
   */
  async function measureAtFrame(trackId: number, frameNum: number): Promise<StereoMeasurement | null> {
    const cams = getMultiCamList();
    if (cams.length < 2) return null;
    const leftCamera = getLeftCameraName();
    const rightCamera = cams.find((c) => c !== leftCamera);
    if (!rightCamera) return null;

    const leftTrack = cameraStore.getPossibleTrack(trackId, leftCamera);
    const rightTrack = cameraStore.getPossibleTrack(trackId, rightCamera);
    const leftLine = lineEndpoints(leftTrack, frameNum);
    const rightLine = lineEndpoints(rightTrack, frameNum);
    // Only one camera has a line yet: a normal in-progress state, not a failure.
    if (!leftLine || !rightLine) return null;

    // Past this point the user has done everything needed, so anything missing
    // is a real problem worth surfacing rather than a silent no-op.
    const rig = await getRig();
    if (!rig) throw new Error('No stereo calibration is available for this dataset.');

    let measurement: StereoMeasurement | null;
    if (leftLine.length > 2 || rightLine.length > 2) {
      const samples = sampleHeadTail(leftLine);
      const matches = await warp(samples, leftCamera, rightCamera, frameNum);
      if (!matches.every((m) => m.accepted && distanceToHeadTail([m.x, m.y], rightLine) <= 5)) {
        throw new Error('Incomplete or inconsistent centerline correspondence.');
      }
      const target = matches.map((m): Point => [m.x, m.y]);
      const pieces = samples.slice(1).map((p, i) => measureLine(rig, [samples[i], p], [target[i], target[i + 1]]));
      const chord = measureLine(rig, [samples[0], samples[samples.length - 1]], [target[0], target[target.length - 1]]);
      if (!chord || chord.length <= 0 || pieces.some((p) => !p || !Number.isFinite(p.length) || p.length <= 0 || p.stereo_rms > 5)) {
        throw new Error('Invalid reconstructed centerline.');
      }
      const length = pieces.reduce((sum, p) => sum + (p as StereoMeasurement).length, 0);
      measurement = {
        ...chord,
        length,
        curved_length: length,
        straight_length: chord.length,
        curvature_ratio: length / chord.length,
        stereo_rms: Math.max(...pieces.map((p) => (p as StereoMeasurement).stereo_rms)),
      };
    } else {
      measurement = measureLine(rig, [leftLine[0], leftLine[1]], [rightLine[0], rightLine[1]]);
    }
    if (!measurement) {
      throw new Error('The two lines could not be triangulated; check that the calibration matches these cameras.');
    }
    if (JSON.stringify(lineEndpoints(leftTrack, frameNum)) !== JSON.stringify(leftLine)
        || JSON.stringify(lineEndpoints(rightTrack, frameNum)) !== JSON.stringify(rightLine)) return null;
    config.ensureMeasurementAttributes?.();
    applyMeasurement(leftTrack, frameNum, measurement);
    applyMeasurement(rightTrack, frameNum, measurement);
    if (leftTrack) config.onChange?.(leftCamera, leftTrack);
    if (rightTrack) config.onChange?.(rightCamera, rightTrack);
    return measurement;
  }

  async function measureAndReport(trackId: number, frameNum: number) {
    const measurement = await measureAtFrame(trackId, frameNum);
    if (measurement) {
      config.onMeasurement?.(measurement);
      updateTrackAverages(trackId);
    }
    return measurement;
  }

  /** Identifies a (source camera -> other camera, frame) pair for matcher caches. */
  function frameKey(sourceCamera: string, otherCamera: string, frameNum: number) {
    return `${sourceCamera}>${otherCamera}@${frameNum}`;
  }

  /** Orient the rig so `sourceCamera` is the source ("left"). */
  function orientRig(rig0: StereoRig, sourceCamera: string) {
    return sourceCamera === getLeftCameraName() ? rig0 : invertRig(rig0);
  }

  /** Run the correspondence search for one set of points, source camera -> other. */
  async function warp(points: Point[], sourceCamera: string, otherCamera: string, frameNum: number, line = false) {
    const [rig0, srcFrame, tgtFrame] = await Promise.all([
      getRig(), getFrame(sourceCamera, frameNum), getFrame(otherCamera, frameNum),
    ]);
    if (!rig0) throw new Error('No stereo calibration is available for this dataset.');
    if (!srcFrame || !tgtFrame) throw new Error('Could not read the frame pixels for both cameras.');
    const matcher = await getMatcher({ width: srcFrame.width, height: srcFrame.height });
    if (!matcher) throw new Error('The stereo matching model could not be loaded.');
    const rig = orientRig(rig0, sourceCamera);

    const match = line && matcher.warpLine ? matcher.warpLine : matcher.warpPoints;
    return match.call(matcher, points, srcFrame, tgtFrame, rig, {
      range: getRange(),
      threshold: config.threshold,
      uniquenessRatio: config.uniquenessRatio,
      frameKey: frameKey(sourceCamera, otherCamera, frameNum),
    });
  }

  /**
   * Let a matcher that works per frame (the foundation method's dense
   * disparity) compute its map for `frameNum` before the user draws anything
   * there. Both warp directions are prepared, the calibration's left camera
   * first. A no-op for matchers without per-frame state. `stillWanted` lets the
   * host drop the work once the viewer has moved on to another frame.
   */
  async function precomputeFrame(frameNum: number, stillWanted: () => boolean = () => true): Promise<void> {
    const cams = getMultiCamList();
    if (cams.length < 2) return;
    const leftCamera = getLeftCameraName();
    const rightCamera = cams.find((c) => c !== leftCamera);
    if (!rightCamera) return;
    const [rig0, leftFrame, rightFrame] = await Promise.all([
      getRig(), getFrame(leftCamera, frameNum), getFrame(rightCamera, frameNum),
    ]);
    if (!rig0 || !leftFrame || !rightFrame || !stillWanted()) return;
    const matcher = await getMatcher({ width: leftFrame.width, height: leftFrame.height });
    if (!matcher?.prepare || !stillWanted()) return;
    await matcher.prepare(frameKey(leftCamera, rightCamera, frameNum), leftFrame, rightFrame, rig0, stillWanted);
    await matcher.prepare(frameKey(rightCamera, leftCamera, frameNum), rightFrame, leftFrame, invertRig(rig0), stillWanted);
  }

  /**
   * Warp the just-completed annotation onto the other camera.
   *
   * Line annotations re-warp whenever the source is edited, so an auto-generated
   * line keeps tracking its source — unless the other side was drawn by a human,
   * which is never overwritten. Boxes and polygons warp once.
   *
   * `quiet` aggregates failures for the bulk path instead of surfacing each one.
   */
  async function handleStereoAnnotationComplete(
    params: StereoAnnotationCompleteParams,
    forceAutoCompute = false,
    quiet = false,
  ): Promise<'transferred' | 'skipped' | 'failed'> {
    if (params.type === 'segmentation') return 'skipped';
    if (params.type === 'point' && isHeadTailPoint(params.key)) {
      getMultiCamList().forEach((camera) => cameraStore.getPossibleTrack(params.trackId, camera)?.invalidateMeasurement(params.frameNum));
    }

    // The warp writes geometry directly rather than re-emitting this event, so
    // reaching here means the user authored this camera's line.
    if (params.type === 'line' || (params.type === 'point' && isHeadTailPoint(params.key))) {
      getMultiCamList().forEach((camera) => cameraStore.getPossibleTrack(params.trackId, camera)?.invalidateMeasurement(params.frameNum));
      cameraStore.getPossibleTrack(params.trackId, params.camera)
        ?.setFeatureAttribute(params.frameNum, STEREO_USER_LINE_ATTR, true);
    }

    const cams = getMultiCamList();
    if (cams.length < 2) return 'skipped';
    const otherCamera = cams.find((c) => c !== params.camera);
    if (!otherCamera) return 'skipped';

    const otherTrack = cameraStore.getPossibleTrack(params.trackId, otherCamera);
    const [otherFeature] = otherTrack ? otherTrack.getFeature(params.frameNum) : [null];
    const otherHasFeature = otherFeature !== null;
    const shouldWarp = forceAutoCompute || autoCompute();

    if (params.type === 'line') {
      const otherIsHuman = otherHasFeature
        && otherFeature?.attributes?.[STEREO_USER_LINE_ATTR] === true;
      if (otherIsHuman || !shouldWarp) {
        // Leave the other side as-is, but refresh the measurement if both sides
        // now have a line.
        if (measureLengths() && otherHasFeature) {
          try {
            await measureAndReport(params.trackId, params.frameNum);
          } catch (err) {
            config.onError?.(`Stereo measurement failed. ${(err as Error).message}`);
          }
        }
        return 'skipped';
      }
    } else if (params.type === 'point') {
      if (!shouldWarp || (!params.insert && !canMapPoint(otherTrack, params.frameNum, params.key, params.camera))) {
        if (isHeadTailPoint(params.key) && measureLengths() && otherHasFeature) {
          try { await measureAndReport(params.trackId, params.frameNum); } catch (err) { config.onError?.(`Stereo measurement failed. ${(err as Error).message}`); }
        }
        return 'skipped';
      }
      if (!otherHasFeature) {
        // One keypoint alone would make a detection of just that point on
        // the other camera; map the detection it belongs to instead.
        const sourceTrack = cameraStore.getPossibleTrack(params.trackId, params.camera);
        const job = detectionTransferJob(sourceTrack, params.frameNum, params.camera);
        if (!job) return 'skipped';
        const result = await handleStereoAnnotationComplete(job, forceAutoCompute, quiet);
        if (result !== 'transferred') return result;
        const mapped = cameraStore.getPossibleTrack(params.trackId, otherCamera);
        const remaining = unmappedPoints(sourceTrack, mapped, params.frameNum);
        for (let i = 0; i < remaining.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await handleStereoAnnotationComplete({
            type: 'point', camera: params.camera, trackId: params.trackId, frameNum: params.frameNum, ...remaining[i],
          }, forceAutoCompute, true);
        }
        return 'transferred';
      }
    } else if (otherHasFeature || !shouldWarp) {
      return 'skipped';
    }

    if (!quiet) config.onStatus?.('Computing stereo correspondence...');
    try {
      if (params.type === 'point') {
        const source = cameraStore.getPossibleTrack(params.trackId, params.camera);
        const targetBefore = pointTargetState(otherTrack, params.frameNum, params.key, params.insert, params.camera);
        const result = await warp([params.point], params.camera, otherCamera, params.frameNum);
        if (result.length !== 1 || !result[0].accepted || !Number.isFinite(result[0].x) || !Number.isFinite(result[0].y)) {
          throw new Error('No confident stereo match for this keypoint.');
        }
        const currentTarget = cameraStore.getPossibleTrack(params.trackId, otherCamera);
        if (!pointUnchanged(source, params.frameNum, params.key, params.point)
            || pointTargetState(currentTarget, params.frameNum, params.key, params.insert, params.camera) !== targetBefore) return 'skipped';
        const track = getOrCreateTrack(params.trackId, params.camera, otherCamera, params.frameNum);
        if (!track) throw new Error('Could not create the target detection.');
        applyMappedPoint(track, params.frameNum, params.key, [result[0].x, result[0].y], params.camera, params.insert);
        config.onChange?.(otherCamera, track);
        if (isHeadTailPoint(params.key) && measureLengths()) {
          try { await measureAndReport(params.trackId, params.frameNum); } catch (err) { config.onError?.(`Point transferred; stereo measurement failed. ${(err as Error).message}`); }
        }
      } else if (params.type === 'box') {
        const [x1, y1, x2, y2] = params.bounds;
        const corners: Point[] = [[x1, y1], [x2, y1], [x2, y2], [x1, y2]];
        const res = await warp(corners, params.camera, otherCamera, params.frameNum);
        if (!res.every((r) => r.accepted)) {
          throw new Error('No confident stereo match for the box corners.');
        }
        const track = getOrCreateTrack(params.trackId, params.camera, otherCamera, params.frameNum);
        if (!track) throw new Error('Could not create the track on the other camera.');
        track.setFeature({
          frame: params.frameNum,
          flick: 0,
          keyframe: true,
          interpolate: false,
          bounds: boundsFromPoints(res.map((r) => [r.x, r.y] as Point)),
        });
        config.onChange?.(otherCamera, track);
      } else if (params.type === 'polygon') {
        const res = await warp(params.polygon, params.camera, otherCamera, params.frameNum);
        if (!res.every((r) => r.accepted)) {
          throw new Error('No confident stereo match for the polygon vertices.');
        }
        const ring = res.map((r) => [r.x, r.y] as Point);
        const [first] = ring;
        const last = ring[ring.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first] as Point);
        const track = getOrCreateTrack(params.trackId, params.camera, otherCamera, params.frameNum);
        if (!track) throw new Error('Could not create the track on the other camera.');
        track.setFeature({
          frame: params.frameNum,
          flick: 0,
          keyframe: true,
          interpolate: false,
          bounds: boundsFromPoints(ring),
        }, [{
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [ring] },
          properties: { key: params.key },
        }] as GeoJSON.Feature<GeoJSON.Geometry>[] as never);
        config.onChange?.(otherCamera, track);
      } else {
        const res = await warp(params.line, params.camera, otherCamera, params.frameNum, true);
        if (!res.every((r) => r.accepted)) {
          throw new Error('No confident stereo match for the line endpoints.');
        }
        const track = getOrCreateTrack(params.trackId, params.camera, otherCamera, params.frameNum);
        if (!track) throw new Error('Could not create the track on the other camera.');
        applyLine(track, params.frameNum, res.map((r): Point => [r.x, r.y]), params.camera);
        config.onChange?.(otherCamera, track);
        if (measureLengths()) await measureAndReport(params.trackId, params.frameNum);
      }
      if (!quiet) config.onStatus?.(null);
      return 'transferred';
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (!quiet) {
        config.onStatus?.(null);
        config.onError?.(`Failed to transfer annotation to the other camera. ${message}`);
      }
      return 'failed';
    }
  }

  /**
   * Recompute the measurement for every frame where a freshly linked pair now
   * has a line on both cameras.
   */
  async function handleStereoTrackLinked(trackId: number): Promise<void> {
    if (!measureLengths()) return;
    const cams = getMultiCamList();
    if (cams.length < 2) return;
    const frames = new Set<number>();
    cams.forEach((camera) => {
      cameraStore.getPossibleTrack(trackId, camera)?.featureIndex
        .forEach((frame) => frames.add(frame));
    });
    let last: StereoMeasurement | null = null;
    // Sequential: each measurement writes to both tracks.
    // eslint-disable-next-line no-restricted-syntax
    for (const frame of [...frames].sort((a, b) => a - b)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const m = await measureAtFrame(trackId, frame);
        if (m) last = m;
      } catch {
        // A frame that cannot be measured should not abort the rest.
      }
    }
    if (last) {
      config.onMeasurement?.(last);
      updateTrackAverages(trackId);
    }
  }

  /**
   * Warp every detection the source camera holds onto the other camera, for the
   * frames given. Frames the other camera already has are left untouched.
   * Returns per-outcome counts so the caller can report a single summary.
   */
  async function warpAllFromCamera(sourceCamera: string, frameNums?: number[]): Promise<{ transferred: number; skipped: number; failed: number }> {
    const counts = { transferred: 0, skipped: 0, failed: 0 };
    const store = cameraStore.camMap.value.get(sourceCamera)?.trackStore;
    if (!store) return counts;

    const jobs: StereoAnnotationCompleteParams[] = [];
    store.annotationMap.forEach((track: Track) => {
      track.featureIndex.forEach((frame: number) => {
        if (frameNums && !frameNums.includes(frame)) return;
        const [feature] = track.getFeature(frame);
        if (!feature) return;
        const line = lineEndpoints(track, frame);
        const base = { camera: sourceCamera, trackId: track.id, frameNum: frame };
        if (line) {
          jobs.push({
            ...base, type: 'line', line, key: HeadTailLineKey,
          });
          return;
        }
        const polyFeat = feature.geometry?.features
          ?.find((f: GeoJSON.Feature) => f.geometry.type === 'Polygon');
        if (polyFeat) {
          const ring = (polyFeat.geometry.coordinates as unknown as Point[][])[0];
          if (ring?.length >= 3) {
            jobs.push({
              ...base, type: 'polygon', polygon: ring, key: polyFeat.properties?.key ?? '',
            });
            return;
          }
        }
        if (feature.bounds) {
          jobs.push({ ...base, type: 'box', bounds: feature.bounds as [number, number, number, number] });
        }
      });
    });

    // Sequential: each job runs a wasm inference and reads frame pixels.
    for (let i = 0; i < jobs.length; i += 1) {
      config.onStatus?.(`Warping detection ${i + 1} of ${jobs.length}...`);
      // eslint-disable-next-line no-await-in-loop
      const result = await handleStereoAnnotationComplete(jobs[i], true, true);
      counts[result] += 1;
    }
    config.onStatus?.(null);
    if (counts.failed) {
      config.onError?.(
        `${counts.failed} of ${jobs.length} detections could not be warped to the other camera.`,
      );
    }
    return counts;
  }

  /**
   * Where one point on `sourceCamera` lands on the other camera at `frameNum`,
   * or null when the match is rejected or the stereo setup is incomplete.
   * Used for linked panning, so it never reports errors to the user.
   */
  async function warpPoint(point: Point, sourceCamera: string, frameNum: number): Promise<Point | null> {
    const otherCamera = getMultiCamList().find((c) => c !== sourceCamera);
    if (!otherCamera || getMultiCamList().length !== 2) return null;
    try {
      const [result] = await warp([point], sourceCamera, otherCamera, frameNum);
      return result?.accepted && Number.isFinite(result.x) && Number.isFinite(result.y)
        ? [result.x, result.y] : null;
    } catch {
      return null;
    }
  }

  return {
    handleStereoAnnotationComplete,
    handleStereoTrackLinked,
    warpAllFromCamera,
    warpPoint,
    measureAtFrame,
    refreshMeasurement: async (id: number, frame: number) => {
      if (measureLengths()) await measureAndReport(id, frame);
    },
    warpPoints: async (points: Point[], camera: string, frame: number, line = false): Promise<(Point | null)[]> => {
      const other = getMultiCamList().find((c) => c !== camera);
      if (!other || getMultiCamList().length !== 2) return [];
      return (await warp(points, camera, other, frame, line)).map((p) => (
        p.accepted && Number.isFinite(p.x) && Number.isFinite(p.y) ? [p.x, p.y] : null
      ));
    },
    precomputeFrame,
  };
}
